import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { useTranslation } from 'react-i18next';
import { changeLanguage, LANGUAGES, getLanguageInfo } from '@/i18n';
import api from "@/utils/api";
import { ChevronRight, Star, Shield, Check, X, Camera, Eye, EyeOff, AlertCircle, Loader2, ShieldCheck } from "lucide-react";
import { KYCStatusCard, KYCVerificationModal, TrustBadge } from "@/components/KYCVerification";
import { SellerTierCard } from "@/components/SellerTier";
import { InspectorReviewModal } from "@/components/InspectorMarketplace";

// Emoji-based menu item with gradient accent
const SettingsCard = ({ emoji, title, description, onClick, badge, badgeColor = "emerald" }) => (
  <button
    onClick={onClick}
    className="w-full text-left p-4 bg-white rounded-xl border border-slate-100 hover:border-emerald-200 hover:shadow-md transition-all duration-200 group"
  >
    <div className="flex items-start gap-4">
      <div className="text-3xl flex-shrink-0 group-hover:scale-110 transition-transform">
        {emoji}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-slate-800 group-hover:text-emerald-600 transition-colors">
            {title}
          </h3>
          {badge && (
            <Badge className={`bg-${badgeColor}-100 text-${badgeColor}-700 text-xs`}>
              {badge}
            </Badge>
          )}
        </div>
        <p className="text-sm text-slate-500 mt-0.5 line-clamp-2">{description}</p>
      </div>
      <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-emerald-500 group-hover:translate-x-1 transition-all flex-shrink-0" />
    </div>
  </button>
);

// Section header with emoji
const SectionHeader = ({ emoji, title, subtitle }) => (
  <div className="mb-4">
    <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
      <span className="text-xl">{emoji}</span>
      {title}
    </h2>
    {subtitle && <p className="text-sm text-slate-500 ml-7">{subtitle}</p>}
  </div>
);

// Stats card with gradient
const StatCard = ({ emoji, value, label, color = "emerald" }) => (
  <div className={`bg-gradient-to-br from-${color}-50 to-${color}-100/50 rounded-xl p-4 text-center border border-${color}-100`}>
    <div className="text-2xl mb-1">{emoji}</div>
    <div className={`text-2xl font-bold text-${color}-600`}>{value}</div>
    <div className="text-xs text-slate-600 font-medium">{label}</div>
  </div>
);

// Profile completion indicator
const ProfileCompletion = ({ percentage }) => (
  <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-100">
    <div className="flex items-center justify-between mb-2">
      <span className="text-sm font-medium text-slate-700">Profile Completion</span>
      <span className="text-sm font-bold text-amber-600">{percentage}%</span>
    </div>
    <Progress value={percentage} className="h-2" />
    <p className="text-xs text-slate-500 mt-2">
      {percentage < 100 ? "Complete your profile to unlock all features! 🎯" : "Your profile is complete! 🎉"}
    </p>
  </div>
);

// Verification badge component
const VerificationBadge = ({ verified, type, onClick }) => (
  <div 
    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-all ${
      verified 
        ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' 
        : 'bg-red-100 text-red-600 hover:bg-red-200'
    }`}
    onClick={onClick}
    title={onClick ? 'Click to verify' : ''}
  >
    {verified ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
    {type} {verified ? 'Verified' : 'Not Verified'}
    {!verified && onClick && <span className="ml-1">→</span>}
  </div>
);

// Inspections Section Component
const InspectionsSection = ({ navigate }) => {
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [stats, setStats] = useState({ total: 0, as_buyer: 0, as_seller: 0, as_inspector: 0 });
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);

  useEffect(() => {
    fetchInspections();
  }, []);

  const fetchInspections = async () => {
    setLoading(true);
    try {
      const res = await api.get('/user/inspections');
      setInspections(res.data.inspections || []);
      setStats(res.data.stats || {});
    } catch (error) {
      console.error('Failed to fetch inspections:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredInspections = activeFilter === 'all' 
    ? inspections 
    : inspections.filter(i => {
        if (activeFilter === 'pending') return i.status === 'pending';
        if (activeFilter === 'active') return ['accepted', 'paid', 'in_progress'].includes(i.status);
        if (activeFilter === 'completed') return i.status === 'completed';
        if (activeFilter === 'buyer') return i.user_role === 'buyer';
        if (activeFilter === 'seller') return i.user_role === 'seller';
        if (activeFilter === 'inspector') return i.user_role === 'inspector';
        return true;
      });

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-amber-100 text-amber-700';
      case 'accepted': return 'bg-blue-100 text-blue-700';
      case 'paid': return 'bg-purple-100 text-purple-700';
      case 'in_progress': return 'bg-cyan-100 text-cyan-700';
      case 'completed': return 'bg-emerald-100 text-emerald-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'buyer': return 'bg-blue-50 text-blue-600 border-blue-200';
      case 'seller': return 'bg-orange-50 text-orange-600 border-orange-200';
      case 'inspector': return 'bg-emerald-50 text-emerald-600 border-emerald-200';
      default: return 'bg-slate-50 text-slate-600';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SectionHeader 
        emoji="🔍" 
        title="My Inspections" 
        subtitle="Track all your inspection requests and activities" 
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 text-center border border-slate-200">
          <div className="text-2xl font-bold text-slate-700">{stats.total}</div>
          <div className="text-xs text-slate-500">Total</div>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 text-center border border-blue-200">
          <div className="text-2xl font-bold text-blue-600">{stats.as_buyer}</div>
          <div className="text-xs text-slate-500">As Buyer</div>
        </div>
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 text-center border border-orange-200">
          <div className="text-2xl font-bold text-orange-600">{stats.as_seller}</div>
          <div className="text-xs text-slate-500">As Seller</div>
        </div>
        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-4 text-center border border-emerald-200">
          <div className="text-2xl font-bold text-emerald-600">{stats.as_inspector}</div>
          <div className="text-xs text-slate-500">As Inspector</div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2">
        {[
          { id: 'all', label: 'All' },
          { id: 'pending', label: 'Pending' },
          { id: 'active', label: 'Active' },
          { id: 'completed', label: 'Completed' },
          { id: 'buyer', label: '🛍️ As Buyer' },
          { id: 'seller', label: '🏪 As Seller' },
          { id: 'inspector', label: '🔍 As Inspector' },
        ].map(filter => (
          <Button
            key={filter.id}
            variant={activeFilter === filter.id ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveFilter(filter.id)}
            className={activeFilter === filter.id ? 'bg-emerald-500' : ''}
          >
            {filter.label}
          </Button>
        ))}
      </div>

      {/* Inspections List */}
      {filteredInspections.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-xl">
          <div className="text-4xl mb-3">🔍</div>
          <p className="text-slate-600">No inspections found</p>
          <p className="text-sm text-slate-400 mt-1">
            {activeFilter === 'all' 
              ? "You haven't been involved in any inspections yet"
              : `No ${activeFilter} inspections`}
          </p>
          <Button 
            variant="outline" 
            className="mt-4"
            onClick={() => navigate('/inspectors')}
          >
            Browse Inspectors
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredInspections.map((inspection) => (
            <Card key={inspection.inspection_id} className="p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-4">
                {/* Listing Image */}
                <div className="w-16 h-16 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0">
                  {inspection.listing_images?.[0] ? (
                    <img src={inspection.listing_images[0]} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-400">🔍</div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="font-semibold text-slate-800 truncate">{inspection.listing_title}</h3>
                    <Badge className={getStatusColor(inspection.status)}>
                      {inspection.status}
                    </Badge>
                    <Badge variant="outline" className={getRoleColor(inspection.user_role)}>
                      {inspection.user_role === 'buyer' && '🛍️'}
                      {inspection.user_role === 'seller' && '🏪'}
                      {inspection.user_role === 'inspector' && '🔍'}
                      {' '}{inspection.user_role}
                    </Badge>
                  </div>
                  
                  <div className="text-sm text-slate-500 space-y-1">
                    {inspection.user_role === 'buyer' && (
                      <p>Inspector: <span className="font-medium text-slate-700">{inspection.inspector_name}</span></p>
                    )}
                    {inspection.user_role === 'seller' && (
                      <>
                        <p>Inspector: <span className="font-medium text-slate-700">{inspection.inspector_name}</span></p>
                        <p>Requested by: <span className="font-medium text-slate-700">{inspection.buyer_name}</span></p>
                      </>
                    )}
                    {inspection.user_role === 'inspector' && (
                      <>
                        <p>Buyer: <span className="font-medium text-slate-700">{inspection.buyer_name}</span></p>
                        <p>Amount: <span className="font-medium text-emerald-600">${inspection.amount?.toFixed(2)}</span></p>
                      </>
                    )}
                    {inspection.scheduled_date && (
                      <p>Scheduled: <span className="font-medium">{new Date(inspection.scheduled_date).toLocaleDateString()}</span></p>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-2">
                  {/* Pay Button for buyers on accepted inspections */}
                  {inspection.user_role === 'buyer' && inspection.status === 'accepted' && inspection.payment_status !== 'paid' && (
                    <Button 
                      size="sm"
                      className="bg-emerald-500 hover:bg-emerald-600"
                      onClick={async () => {
                        try {
                          const res = await api.post(`/inspections/${inspection.inspection_id}/create-payment`);
                          if (res.data.checkout_url) {
                            window.location.href = res.data.checkout_url;
                          }
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Failed to create payment');
                        }
                      }}
                    >
                      💳 Pay ${inspection.amount?.toFixed(2)}
                    </Button>
                  )}
                  
                  {/* Review Button for buyers on completed inspections */}
                  {inspection.can_review && (
                    <Button 
                      size="sm"
                      className="bg-amber-500 hover:bg-amber-600"
                      onClick={() => {
                        setSelectedInspection(inspection);
                        setShowReviewModal(true);
                      }}
                    >
                      <Star className="h-4 w-4 mr-1" />
                      Review
                    </Button>
                  )}
                  {inspection.has_reviewed && (
                    <Badge className="bg-emerald-100 text-emerald-700">
                      <Check className="h-3 w-3 mr-1" />
                      Reviewed
                    </Badge>
                  )}
                  
                  {/* Message Button */}
                  {inspection.status !== 'cancelled' && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => navigate('/messages')}
                    >
                      💬 Message
                    </Button>
                  )}
                </div>
              </div>

              {/* Progress Bar for active inspections */}
              {['accepted', 'paid', 'in_progress'].includes(inspection.status) && (
                <div className="mt-4 pt-4 border-t">
                  <div className="flex items-center justify-between text-xs text-slate-500 mb-2">
                    <span>Progress</span>
                    <span>
                      {inspection.status === 'accepted' && '25%'}
                      {inspection.status === 'paid' && '50%'}
                      {inspection.status === 'in_progress' && '75%'}
                    </span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-emerald-500 rounded-full transition-all"
                      style={{ 
                        width: inspection.status === 'accepted' ? '25%' 
                             : inspection.status === 'paid' ? '50%' 
                             : '75%' 
                      }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-slate-400 mt-1">
                    <span>Requested</span>
                    <span>Accepted</span>
                    <span>Paid</span>
                    <span>In Progress</span>
                    <span>Complete</span>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Review Modal */}
      {selectedInspection && (
        <InspectorReviewModal
          isOpen={showReviewModal}
          onClose={() => {
            setShowReviewModal(false);
            setSelectedInspection(null);
          }}
          inspection={selectedInspection}
          onSuccess={() => {
            fetchInspections();
            setShowReviewModal(false);
            setSelectedInspection(null);
          }}
        />
      )}
    </div>
  );
};

const AccountPage = ({ user, onLogout, userCurrency, setUserCurrency }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const [stats, setStats] = useState({
    watchlistCount: 0,
    purchasesCount: 0,
    bidsCount: 0,
    listingsCount: 0,
    salesCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Dialog states
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showSecurity, setShowSecurity] = useState(false);
  const [showAddresses, setShowAddresses] = useState(false);
  const [showPayments, setShowPayments] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showLanguage, setShowLanguage] = useState(false);
  const [showCurrency, setShowCurrency] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [showOffers, setShowOffers] = useState(false);
  const [offers, setOffers] = useState({ received: [], sent: [], pending_received: 0, pending_sent: 0 });
  const [loadingOffers, setLoadingOffers] = useState(false);
  const [showKYCModal, setShowKYCModal] = useState(false);

  // Handle URL parameters for deep linking
  useEffect(() => {
    const tab = searchParams.get('tab');
    const section = searchParams.get('section');
    
    if (tab) {
      setActiveTab(tab);
    }
    
    if (section === 'offers') {
      setShowOffers(true);
      fetchOffers();
    }
  }, [searchParams]);
  
  // Form states
  const [profileForm, setProfileForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    bio: user?.bio || '',
    location: user?.location || '',
  });
  
  const [securityForm, setSecurityForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    twoFactorEnabled: false,
  });
  
  const [showPassword, setShowPassword] = useState(false);
  
  // Preferences state
  const [preferences, setPreferences] = useState({
    emailNotifications: true,
    pushNotifications: true,
    smsNotifications: false,
    marketingEmails: false,
    bidAlerts: true,
    watchlistAlerts: true,
    priceDropAlerts: true,
    newListingAlerts: false,
  });
  
  const [privacySettings, setPrivacySettings] = useState({
    showProfile: true,
    showActivity: true,
    showWatchlist: false,
    allowMessages: true,
    shareDataForAds: false,
    aiTraining: false,
  });
  
  // Addresses state
  const [addresses, setAddresses] = useState([]);
  const [newAddress, setNewAddress] = useState({ 
    label: 'Home',
    street: '', 
    city: '', 
    state: '', 
    zip: '', 
    country: 'United States',
    isDefault: false 
  });
  
  // Payment methods
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paypalConnected, setPaypalConnected] = useState(false);
  const [paypalVerified, setPaypalVerified] = useState(false);
  const [paypalEmail, setPaypalEmail] = useState('');
  const [paypalPayerId, setPaypalPayerId] = useState('');
  const [connectingPaypal, setConnectingPaypal] = useState(false);
  
  // Seller Payout states
  const [showSellerPayouts, setShowSellerPayouts] = useState(false);
  const [sellerBalance, setSellerBalance] = useState(null);
  const [payoutSettings, setPayoutSettings] = useState(null);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [loadingPayouts, setLoadingPayouts] = useState(false);
  const [requestingPayout, setRequestingPayout] = useState(false);
  const [connectingStripe, setConnectingStripe] = useState(false);
  const [sendingVerification, setSendingVerification] = useState(false);

  // Handle PayPal OAuth callback from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paypalCallback = urlParams.get('paypal_callback');
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    
    if (paypalCallback && code && state && user) {
      // Exchange code for PayPal account info
      const handlePayPalCallback = async () => {
        try {
          setConnectingPaypal(true);
          const response = await api.post('/seller/paypal/callback', {
            code,
            state,
            origin: window.location.origin
          });
          
          if (response.data.success) {
            setPaypalConnected(true);
            setPaypalVerified(true);
            setPaypalEmail(response.data.paypal_email);
            setPaypalPayerId(response.data.paypal_payer_id);
            toast.success(`PayPal connected: ${response.data.paypal_email} 🎉`);
          }
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to connect PayPal');
        } finally {
          setConnectingPaypal(false);
          // Clean up URL
          window.history.replaceState({}, '', '/account');
        }
      };
      
      handlePayPalCallback();
    }
  }, [user]);

  // Connect PayPal via OAuth
  const handleConnectPayPal = async () => {
    try {
      setConnectingPaypal(true);
      const response = await api.post('/seller/paypal/auth-url', {
        origin: window.location.origin
      });
      
      if (response.data.auth_url) {
        // Redirect to PayPal for authorization
        window.location.href = response.data.auth_url;
      }
    } catch (error) {
      toast.error('Failed to initiate PayPal connection');
      setConnectingPaypal(false);
    }
  };

  // Disconnect PayPal
  const handleDisconnectPayPal = async () => {
    if (!confirm('Are you sure you want to disconnect your PayPal account?')) return;
    
    try {
      await api.post('/seller/paypal/disconnect');
      setPaypalConnected(false);
      setPaypalVerified(false);
      setPaypalEmail('');
      setPaypalPayerId('');
      toast.success('PayPal disconnected');
    } catch (error) {
      toast.error('Failed to disconnect PayPal');
    }
  };

  // Fetch user offers
  const fetchOffers = async () => {
    setLoadingOffers(true);
    try {
      const res = await api.get('/offers');
      setOffers(res.data);
    } catch (error) {
      console.error('Failed to fetch offers:', error);
      toast.error('Failed to load offers');
    } finally {
      setLoadingOffers(false);
    }
  };

  // Respond to an offer (handles both regular item offers and bundle offers)
  const handleOfferResponse = async (offer, status, counterAmount = null) => {
    try {
      const isBundle = offer.message_type === 'bundle_offer' && offer.bundle_offer_id;
      if (isBundle) {
        // Bundle offer endpoint
        const action = status === 'accepted' ? 'accept' : status === 'declined' ? 'decline' : 'counter';
        await api.post(`/cart/bundle-offers/${offer.bundle_offer_id}/respond`, {
          action,
          counter_price: counterAmount
        });
      } else {
        // Per-listing message offer endpoint
        await api.post(`/messages/offer/${offer.message_id}/respond`, {
          status,
          counter_amount: counterAmount
        });
      }
      toast.success(status === 'accepted' ? 'Offer accepted!' : status === 'declined' ? 'Offer declined' : 'Counter offer sent!');
      fetchOffers(); // Refresh offers
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to respond to offer');
    }
  };

  // Send email verification
  const handleSendVerificationEmail = async () => {
    if (user?.email_verified) {
      toast.success('Your email is already verified! ✓');
      return;
    }
    
    try {
      setSendingVerification(true);
      await api.post('/auth/resend-verification', {
        email: user?.email
      });
      toast.success('Verification email sent! 📧 Check your inbox (and spam folder)');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send verification email';
      toast.error(message);
    } finally {
      setSendingVerification(false);
    }
  };

  useEffect(() => {
    if (!user) return;

    const fetchStats = async () => {
      try {
        const [watchlistRes, bidsRes, listingsRes, ordersRes, offersRes] = await Promise.all([
          api.get('/watchlist').catch(() => ({ data: { listings: [] } })),
          api.get('/my-bids').catch(() => ({ data: { bids: [] } })),
          api.get('/my-listings').catch(() => ({ data: { listings: [] } })),
          api.get('/orders').catch(() => ({ data: [] })),
          api.get('/offers').catch(() => ({ data: { received: [], sent: [], pending_received: 0, pending_sent: 0 } })),
        ]);

        setStats({
          watchlistCount: watchlistRes.data?.listings?.length || 0,
          bidsCount: bidsRes.data?.bids?.length || 0,
          listingsCount: listingsRes.data?.listings?.length || 0,
          purchasesCount: Array.isArray(ordersRes.data) ? ordersRes.data.filter(o => o.buyer_id === user.user_id).length : 0,
          salesCount: Array.isArray(ordersRes.data) ? ordersRes.data.filter(o => o.seller_id === user.user_id).length : 0,
        });
        
        // Set offers data for badge counts
        if (offersRes.data) {
          setOffers(offersRes.data);
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    // Fetch payout settings on load to check PayPal connection status
    const fetchPayoutSettings = async () => {
      try {
        const response = await api.get('/seller/payout-settings');
        setPayoutSettings(response.data);
        setPaypalConnected(response.data?.paypal_connected || false);
        setPaypalVerified(response.data?.paypal_verified || false);
        setPaypalEmail(response.data?.paypal_email || '');
        setPaypalPayerId(response.data?.paypal_payer_id || '');
      } catch (error) {
        console.error('Error fetching payout settings:', error);
      }
    };

    // Load saved preferences
    const savedPrefs = localStorage.getItem('userPreferences');
    if (savedPrefs) setPreferences(JSON.parse(savedPrefs));
    
    const savedAddresses = localStorage.getItem('userAddresses');
    if (savedAddresses) setAddresses(JSON.parse(savedAddresses));

    fetchStats();
    fetchPayoutSettings();
  }, [user]);

  // Calculate profile completion
  const calculateProfileCompletion = () => {
    let score = 0;
    if (user?.name) score += 20;
    if (user?.email) score += 20;
    if (user?.email_verified) score += 15;
    if (addresses.length > 0) score += 15;
    if (paymentMethods.length > 0 || paypalConnected) score += 15;
    if (profileForm.phone) score += 10;
    if (profileForm.bio) score += 5;
    return Math.min(score, 100);
  };

  const handleSaveProfile = () => {
    toast.success('Profile updated successfully! ✨');
    setShowEditProfile(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('userPreferences', JSON.stringify(preferences));
    toast.success('Notification preferences saved! 🔔');
    setShowNotifications(false);
  };

  const handleSavePrivacy = () => {
    localStorage.setItem('privacySettings', JSON.stringify(privacySettings));
    toast.success('Privacy settings updated! 🔒');
    setShowPrivacy(false);
  };

  const handleAddAddress = () => {
    if (newAddress.street && newAddress.city && newAddress.zip) {
      const updatedAddresses = [...addresses, { ...newAddress, id: Date.now() }];
      setAddresses(updatedAddresses);
      localStorage.setItem('userAddresses', JSON.stringify(updatedAddresses));
      setNewAddress({ label: 'Home', street: '', city: '', state: '', zip: '', country: 'United States', isDefault: false });
      toast.success('Address added! 📍');
    } else {
      toast.error('Please fill in all required fields');
    }
  };

  const handleDeleteAddress = (id) => {
    const updatedAddresses = addresses.filter(a => a.id !== id);
    setAddresses(updatedAddresses);
    localStorage.setItem('userAddresses', JSON.stringify(updatedAddresses));
    toast.success('Address removed');
  };

  const handleCurrencyChange = (currency) => {
    const symbols = { USD: '$', EUR: '€', GBP: '£', CAD: 'C$', AUD: 'A$' };
    if (setUserCurrency) {
      setUserCurrency({ code: currency, symbol: symbols[currency] || '$' });
      localStorage.setItem('userCurrency', JSON.stringify({ code: currency }));
      toast.success(`Currency changed to ${currency} 💱`);
    }
    setShowCurrency(false);
  };

  // Fetch seller payout data
  const fetchSellerPayoutData = async () => {
    try {
      setLoadingPayouts(true);
      const [balanceRes, settingsRes, historyRes] = await Promise.all([
        api.get('/seller/balance'),
        api.get('/seller/payout-settings'),
        api.get('/seller/payouts')
      ]);
      setSellerBalance(balanceRes.data);
      setPayoutSettings(settingsRes.data);
      setPayoutHistory(historyRes.data.payouts || []);
    } catch (error) {
      console.error('Error fetching payout data:', error);
      toast.error('Failed to load payout data');
    } finally {
      setLoadingPayouts(false);
    }
  };

  const handleOpenSellerPayouts = () => {
    setShowSellerPayouts(true);
    fetchSellerPayoutData();
  };

  const handleUpdatePayoutSettings = async (paypalEmail) => {
    try {
      await api.post('/seller/payout-settings', {
        payout_method: 'paypal',
        paypal_email: paypalEmail
      });
      toast.success('PayPal email updated! 💰');
      fetchSellerPayoutData();
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const handleConnectStripe = async () => {
    try {
      setConnectingStripe(true);
      const response = await api.post('/seller/connect-stripe', {
        origin_url: window.location.origin
      });
      // Redirect to Stripe onboarding
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to connect Stripe');
      setConnectingStripe(false);
    }
  };

  const handleRequestPayout = async () => {
    if (!sellerBalance || sellerBalance.available_balance <= 0) {
      toast.error('No available balance to withdraw');
      return;
    }
    
    try {
      setRequestingPayout(true);
      const response = await api.post('/seller/request-payout', {
        amount: sellerBalance.available_balance
      });
      toast.success(`Payout requested! ${response.data.message}`);
      fetchSellerPayoutData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request payout');
    } finally {
      setRequestingPayout(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    
    try {
      setDeletingAccount(true);
      await api.delete('/auth/account');
      toast.success('Account successfully deleted');
      localStorage.removeItem('token');
      setShowDeleteAccount(false);
      navigate('/');
      window.location.reload();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to delete account';
      toast.error(errorMessage);
    } finally {
      setDeletingAccount(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen pt-52 pb-16 flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <div className="text-6xl mb-4">🔐</div>
          <h2 className="text-xl font-bold mb-2">Sign In Required</h2>
          <p className="text-slate-500 mb-4">Please sign in to access your account settings</p>
          <Button onClick={() => navigate('/')} className="bg-emerald-500 hover:bg-emerald-600">
            Go to Homepage
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-52 pb-16 bg-gradient-to-b from-slate-50 to-white" data-testid="account-page">
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        
        {/* Profile Header */}
        <div className="bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 rounded-2xl p-6 md:p-8 text-white mb-6 shadow-lg">
          <div className="flex flex-col md:flex-row items-center gap-6">
            {/* Avatar */}
            <div className="relative">
              <Avatar className="h-24 w-24 border-4 border-white/30 shadow-xl">
                <AvatarImage src={user?.avatar} alt={user?.name} />
                <AvatarFallback className="bg-white/20 text-white text-2xl font-bold">
                  {user?.name?.charAt(0)?.toUpperCase() || '👤'}
                </AvatarFallback>
              </Avatar>
              <button 
                className="absolute bottom-0 right-0 bg-white text-emerald-600 rounded-full p-2 shadow-lg hover:scale-110 transition-transform"
                onClick={() => setShowEditProfile(true)}
              >
                <Camera className="h-4 w-4" />
              </button>
            </div>
            
            {/* User Info */}
            <div className="flex-1 text-center md:text-left">
              <h1 className="text-2xl md:text-3xl font-bold flex items-center justify-center md:justify-start gap-2">
                {user?.name || 'Welcome User'} 
                {user?.email_verified && <Shield className="h-5 w-5 text-yellow-300" />}
              </h1>
              <p className="text-emerald-100 mt-1">{user?.email}</p>
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mt-3">
                <VerificationBadge 
                  verified={user?.email_verified} 
                  type="Email" 
                  onClick={!user?.email_verified ? handleSendVerificationEmail : undefined}
                />
                {sendingVerification && <Loader2 className="h-4 w-4 animate-spin text-white" />}
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
                  paypalVerified || payoutSettings?.stripe_connected
                    ? 'bg-emerald-100 text-emerald-700' 
                    : 'bg-amber-100 text-amber-700'
                }`}>
                  {paypalVerified || payoutSettings?.stripe_connected ? <Check className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
                  Payout {paypalVerified || payoutSettings?.stripe_connected ? 'Verified ✓' : 'Not Setup'}
                </div>
                <Badge className="bg-white/20 text-white">
                  ⭐ Member since {new Date(user?.created_at || Date.now()).getFullYear()}
                </Badge>
              </div>
            </div>
            
            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center bg-white/10 rounded-xl p-3 backdrop-blur-sm">
                <div className="text-2xl font-bold">{stats.listingsCount}</div>
                <div className="text-xs text-emerald-100">Listings</div>
              </div>
              <div className="text-center bg-white/10 rounded-xl p-3 backdrop-blur-sm">
                <div className="text-2xl font-bold">{stats.salesCount}</div>
                <div className="text-xs text-emerald-100">Sales</div>
              </div>
              <div className="text-center bg-white/10 rounded-xl p-3 backdrop-blur-sm">
                <div className="text-2xl font-bold">{stats.purchasesCount}</div>
                <div className="text-xs text-emerald-100">Purchases</div>
              </div>
            </div>
          </div>
        </div>

        {/* Profile Completion */}
        <ProfileCompletion percentage={calculateProfileCompletion()} />

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 my-6">
          <StatCard emoji="❤️" value={stats.watchlistCount} label="Watchlist" color="rose" />
          <StatCard emoji="🔨" value={stats.bidsCount} label="Active Bids" color="amber" />
          <StatCard emoji="📦" value={stats.listingsCount} label="Listings" color="blue" />
          <StatCard emoji="💰" value={stats.salesCount} label="Items Sold" color="emerald" />
          <StatCard emoji="🛒" value={stats.purchasesCount} label="Purchases" color="purple" />
        </div>

        {/* Main Content */}
        <Tabs defaultValue="personal" className="mt-8">
          <TabsList className="grid grid-cols-5 gap-2 bg-slate-100 p-1 rounded-xl mb-6">
            <TabsTrigger value="personal" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow">
              👤 Personal
            </TabsTrigger>
            <TabsTrigger value="selling" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow">
              🏪 Selling
            </TabsTrigger>
            <TabsTrigger value="buying" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow">
              🛍️ Buying
            </TabsTrigger>
            <TabsTrigger value="inspections" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow">
              🔍 Inspections
            </TabsTrigger>
            <TabsTrigger value="preferences" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow">
              ⚙️ Settings
            </TabsTrigger>
          </TabsList>

          {/* Personal Tab */}
          <TabsContent value="personal" className="space-y-6">
            {/* Trust & Safety Section */}
            <SectionHeader 
              emoji="🛡️" 
              title="Trust & Safety" 
              subtitle="Verify your identity to build trust and unlock all features" 
            />
            
            <KYCStatusCard 
              user={user} 
              onStartVerification={() => setShowKYCModal(true)} 
            />
            
            <SectionHeader emoji="👤" title="Personal Information" subtitle="Manage your account details and security" />
            
            <div className="grid md:grid-cols-2 gap-4">
              <SettingsCard
                emoji="✏️"
                title="Edit Profile"
                description="Update your name, bio, and profile picture"
                onClick={() => setShowEditProfile(true)}
              />
              <SettingsCard
                emoji="🔐"
                title="Sign-in & Security"
                description="Password, 2FA, and login activity"
                onClick={() => setShowSecurity(true)}
              />
              <SettingsCard
                emoji="📍"
                title="Addresses"
                description="Manage shipping and billing addresses"
                onClick={() => setShowAddresses(true)}
                badge={addresses.length > 0 ? `${addresses.length} saved` : null}
              />
              <SettingsCard
                emoji="💰"
                title="Payout Methods"
                description="Set up how to receive money from sales"
                onClick={() => setShowPayments(true)}
                badge={paypalVerified || payoutSettings?.stripe_connected ? "Verified ✓" : "Setup Required"}
                badgeColor={paypalVerified || payoutSettings?.stripe_connected ? "emerald" : "amber"}
              />
              <SettingsCard
                emoji="⭐"
                title="Feedback & Reviews"
                description="View your seller and buyer ratings"
                onClick={() => setShowFeedback(true)}
                badge="4.9"
                badgeColor="amber"
              />
              <SettingsCard
                emoji="📊"
                title="Request Your Data"
                description="Download a copy of your BidZuu data"
                onClick={() => toast.info('Data export requested! You\'ll receive an email shortly. 📧')}
              />
            </div>
          </TabsContent>

          {/* Selling Tab */}
          <TabsContent value="selling" className="space-y-6">
            <SectionHeader emoji="🏪" title="Seller Hub" subtitle="Tools and settings for selling on BidZuu" />
            
            {/* Seller Tier Card */}
            <SellerTierCard className="mb-4" />
            
            <div className="grid md:grid-cols-2 gap-4">
              <SettingsCard
                emoji="📈"
                title="Seller Dashboard"
                description="Analytics, sales reports, and performance"
                onClick={() => navigate('/dashboard?tab=sales')}
                badge={stats.salesCount > 0 ? `${stats.salesCount} sales` : null}
              />
              <SettingsCard
                emoji="📦"
                title="My Listings"
                description="Manage your active and draft listings"
                onClick={() => navigate('/dashboard?tab=listings')}
                badge={stats.listingsCount > 0 ? `${stats.listingsCount} active` : null}
              />
              <SettingsCard
                emoji="🎁"
                title="Offers Received"
                description="Review and respond to buyer offers"
                onClick={() => { setShowOffers(true); fetchOffers(); }}
                badge={offers.pending_received > 0 ? `${offers.pending_received} pending` : null}
                badgeColor="amber"
              />
              <SettingsCard
                emoji="💵"
                title="Seller Payouts"
                description="Earnings, pending payments, and history"
                onClick={handleOpenSellerPayouts}
                badge={sellerBalance?.available_balance > 0 ? `$${sellerBalance.available_balance.toFixed(2)}` : null}
                badgeColor="emerald"
              />
              <SettingsCard
                emoji="🚚"
                title="Shipping Settings"
                description="Shipping rates, carriers, and labels"
                onClick={() => toast.info('Shipping settings coming soon! 📦')}
              />
              <SettingsCard
                emoji="🏆"
                title="Seller Badges"
                description="Achievements and trust badges"
                onClick={() => toast.info('Keep selling to earn badges! 🎯')}
              />
              <SettingsCard
                emoji="📣"
                title="Promotions & Ads"
                description="Boost your listings and run promotions"
                onClick={() => toast.info('Promotion tools coming soon! 🚀')}
              />
            </div>
          </TabsContent>

          {/* Buying Tab */}
          <TabsContent value="buying" className="space-y-6">
            <SectionHeader emoji="🛍️" title="Buyer Center" subtitle="Track your orders and manage purchases" />
            
            <div className="grid md:grid-cols-2 gap-4">
              <SettingsCard
                emoji="❤️"
                title="Watchlist"
                description="Items you're keeping an eye on"
                onClick={() => navigate('/dashboard?tab=watchlist')}
                badge={stats.watchlistCount > 0 ? `${stats.watchlistCount} items` : null}
                badgeColor="rose"
              />
              <SettingsCard
                emoji="🔨"
                title="My Bids"
                description="Active bids and bid history"
                onClick={() => navigate('/dashboard?tab=bids')}
                badge={stats.bidsCount > 0 ? `${stats.bidsCount} active` : null}
                badgeColor="amber"
              />
              <SettingsCard
                emoji="🛒"
                title="Purchase History"
                description="Past orders and receipts"
                onClick={() => navigate('/dashboard?tab=purchases')}
                badge={stats.purchasesCount > 0 ? `${stats.purchasesCount} orders` : null}
              />
              <SettingsCard
                emoji="📬"
                title="Saved Searches"
                description="Get alerts for new matching items"
                onClick={() => toast.info('Saved searches feature coming soon! 🔍')}
              />
              <SettingsCard
                emoji="🎁"
                title="Offers"
                description="View sent and received offers"
                onClick={() => { setShowOffers(true); fetchOffers(); }}
                badge={offers.pending_received > 0 ? `${offers.pending_received} pending` : null}
                badgeColor="amber"
              />
              <SettingsCard
                emoji="🛡️"
                title="Buyer Protection"
                description="Resolution center and refund requests"
                onClick={() => navigate('/help')}
              />
            </div>
          </TabsContent>

          {/* Inspections Tab */}
          <TabsContent value="inspections" className="space-y-6">
            <InspectionsSection navigate={navigate} />
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="preferences" className="space-y-6">
            <SectionHeader emoji="⚙️" title="Account Preferences" subtitle="Customize your BidZuu experience" />
            
            <div className="grid md:grid-cols-2 gap-4">
              <SettingsCard
                emoji="🔔"
                title="Notifications"
                description="Email, push, and SMS preferences"
                onClick={() => setShowNotifications(true)}
              />
              <SettingsCard
                emoji="🔒"
                title="Privacy Settings"
                description="Control what others see about you"
                onClick={() => setShowPrivacy(true)}
              />
              <SettingsCard
                emoji="🌐"
                title="Language"
                description={`Currently: ${getLanguageInfo(i18n.language)?.name || 'English'}`}
                onClick={() => setShowLanguage(true)}
              />
              <SettingsCard
                emoji="💱"
                title="Currency"
                description={`Currently: ${userCurrency?.code || 'USD'}`}
                onClick={() => setShowCurrency(true)}
              />
              <SettingsCard
                emoji="🤖"
                title="AI Preferences"
                description="Control AI training and recommendations"
                onClick={() => setShowPrivacy(true)}
              />
              <SettingsCard
                emoji="📢"
                title="Ad Preferences"
                description="Manage personalized advertisements"
                onClick={() => toast.info('Ad preferences updated! 📊')}
              />
              <SettingsCard
                emoji="❓"
                title="Help & Support"
                description="FAQs, contact us, and tutorials"
                onClick={() => navigate('/help')}
              />
              <SettingsCard
                emoji="🚪"
                title="Sign Out"
                description="Log out of your account"
                onClick={onLogout}
              />
            </div>
            
            {/* Danger Zone */}
            <div className="mt-8 p-4 border-2 border-dashed border-red-200 rounded-xl bg-red-50/50">
              <h3 className="text-red-600 font-semibold flex items-center gap-2 mb-3">
                ⚠️ Danger Zone
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <button
                  onClick={() => setShowDeleteAccount(true)}
                  className="p-4 text-left bg-white rounded-xl border border-red-200 hover:border-red-400 hover:bg-red-50 transition-all group"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">🗑️</span>
                    <div>
                      <h4 className="font-semibold text-red-600">Close Account</h4>
                      <p className="text-sm text-slate-500">Permanently delete your account</p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Edit Profile Dialog */}
        <Dialog open={showEditProfile} onOpenChange={setShowEditProfile}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                ✏️ Edit Profile
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="flex justify-center mb-4">
                <div className="relative">
                  <Avatar className="h-20 w-20">
                    <AvatarImage src={user?.avatar} />
                    <AvatarFallback className="bg-emerald-100 text-emerald-600 text-2xl">
                      {user?.name?.charAt(0)?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <button className="absolute bottom-0 right-0 bg-emerald-500 text-white rounded-full p-1.5">
                    <Camera className="h-3 w-3" />
                  </button>
                </div>
              </div>
              <div>
                <Label>Full Name</Label>
                <Input 
                  value={profileForm.name} 
                  onChange={(e) => setProfileForm({...profileForm, name: e.target.value})}
                  placeholder="Your name"
                />
              </div>
              <div>
                <Label>Phone Number</Label>
                <Input 
                  value={profileForm.phone} 
                  onChange={(e) => setProfileForm({...profileForm, phone: e.target.value})}
                  placeholder="+1 (555) 123-4567"
                />
              </div>
              <div>
                <Label>Location</Label>
                <Input 
                  value={profileForm.location} 
                  onChange={(e) => setProfileForm({...profileForm, location: e.target.value})}
                  placeholder="City, State"
                />
              </div>
              <div>
                <Label>Bio</Label>
                <Textarea 
                  value={profileForm.bio} 
                  onChange={(e) => setProfileForm({...profileForm, bio: e.target.value})}
                  placeholder="Tell others about yourself..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditProfile(false)}>Cancel</Button>
              <Button onClick={handleSaveProfile} className="bg-emerald-500 hover:bg-emerald-600">
                Save Changes ✨
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Security Dialog */}
        <Dialog open={showSecurity} onOpenChange={setShowSecurity}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                🔐 Sign-in & Security
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Current Password</Label>
                <div className="relative">
                  <Input 
                    type={showPassword ? "text" : "password"}
                    value={securityForm.currentPassword}
                    onChange={(e) => setSecurityForm({...securityForm, currentPassword: e.target.value})}
                  />
                  <button 
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label>New Password</Label>
                <Input 
                  type="password"
                  value={securityForm.newPassword}
                  onChange={(e) => setSecurityForm({...securityForm, newPassword: e.target.value})}
                />
              </div>
              <div>
                <Label>Confirm New Password</Label>
                <Input 
                  type="password"
                  value={securityForm.confirmPassword}
                  onChange={(e) => setSecurityForm({...securityForm, confirmPassword: e.target.value})}
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium">Two-Factor Authentication</p>
                  <p className="text-sm text-slate-500">Extra security for your account</p>
                </div>
                <Switch 
                  checked={securityForm.twoFactorEnabled}
                  onCheckedChange={(checked) => setSecurityForm({...securityForm, twoFactorEnabled: checked})}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSecurity(false)}>Cancel</Button>
              <Button onClick={() => { toast.success('Security settings updated! 🔒'); setShowSecurity(false); }} className="bg-emerald-500 hover:bg-emerald-600">
                Update Security
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Addresses Dialog */}
        <Dialog open={showAddresses} onOpenChange={setShowAddresses}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                📍 Manage Addresses
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4 max-h-96 overflow-y-auto">
              {addresses.length > 0 && (
                <div className="space-y-2">
                  {addresses.map((addr) => (
                    <div key={addr.id} className="p-3 bg-slate-50 rounded-lg flex justify-between items-start">
                      <div>
                        <p className="font-medium flex items-center gap-2">
                          {addr.label === 'Home' ? '🏠' : addr.label === 'Work' ? '🏢' : '📍'} {addr.label}
                          {addr.isDefault && <Badge className="bg-emerald-100 text-emerald-700 text-xs">Default</Badge>}
                        </p>
                        <p className="text-sm text-slate-600">{addr.street}</p>
                        <p className="text-sm text-slate-600">{addr.city}, {addr.state} {addr.zip}</p>
                        <p className="text-sm text-slate-500">{addr.country}</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteAddress(addr.id)}>
                        <X className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">➕ Add New Address</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Label>Label</Label>
                    <Select value={newAddress.label} onValueChange={(v) => setNewAddress({...newAddress, label: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Home">🏠 Home</SelectItem>
                        <SelectItem value="Work">🏢 Work</SelectItem>
                        <SelectItem value="Other">📍 Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label>Street Address</Label>
                    <Input 
                      value={newAddress.street}
                      onChange={(e) => setNewAddress({...newAddress, street: e.target.value})}
                      placeholder="123 Main St"
                    />
                  </div>
                  <div>
                    <Label>City</Label>
                    <Input 
                      value={newAddress.city}
                      onChange={(e) => setNewAddress({...newAddress, city: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>State</Label>
                    <Input 
                      value={newAddress.state}
                      onChange={(e) => setNewAddress({...newAddress, state: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>ZIP Code</Label>
                    <Input 
                      value={newAddress.zip}
                      onChange={(e) => setNewAddress({...newAddress, zip: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Country</Label>
                    <Select value={newAddress.country} onValueChange={(v) => setNewAddress({...newAddress, country: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="United States">🇺🇸 United States</SelectItem>
                        <SelectItem value="Canada">🇨🇦 Canada</SelectItem>
                        <SelectItem value="United Kingdom">🇬🇧 United Kingdom</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button onClick={handleAddAddress} className="w-full mt-4 bg-emerald-500 hover:bg-emerald-600">
                  Add Address 📍
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Notifications Dialog */}
        <Dialog open={showNotifications} onOpenChange={setShowNotifications}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                🔔 Notification Preferences
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium">📧 Email Notifications</p>
                  <p className="text-sm text-slate-500">Receive updates via email</p>
                </div>
                <Switch checked={preferences.emailNotifications} onCheckedChange={(v) => setPreferences({...preferences, emailNotifications: v})} />
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium">📱 Push Notifications</p>
                  <p className="text-sm text-slate-500">Mobile app notifications</p>
                </div>
                <Switch checked={preferences.pushNotifications} onCheckedChange={(v) => setPreferences({...preferences, pushNotifications: v})} />
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium">💬 SMS Notifications</p>
                  <p className="text-sm text-slate-500">Text message alerts</p>
                </div>
                <Switch checked={preferences.smsNotifications} onCheckedChange={(v) => setPreferences({...preferences, smsNotifications: v})} />
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium">🔨 Bid Alerts</p>
                  <p className="text-sm text-slate-500">When you're outbid</p>
                </div>
                <Switch checked={preferences.bidAlerts} onCheckedChange={(v) => setPreferences({...preferences, bidAlerts: v})} />
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium">❤️ Watchlist Alerts</p>
                  <p className="text-sm text-slate-500">Price drops and ending soon</p>
                </div>
                <Switch checked={preferences.watchlistAlerts} onCheckedChange={(v) => setPreferences({...preferences, watchlistAlerts: v})} />
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium">📢 Marketing Emails</p>
                  <p className="text-sm text-slate-500">Deals and promotions</p>
                </div>
                <Switch checked={preferences.marketingEmails} onCheckedChange={(v) => setPreferences({...preferences, marketingEmails: v})} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNotifications(false)}>Cancel</Button>
              <Button onClick={handleSavePreferences} className="bg-emerald-500 hover:bg-emerald-600">
                Save Preferences 🔔
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Privacy Dialog */}
        <Dialog open={showPrivacy} onOpenChange={setShowPrivacy}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                🔒 Privacy Settings
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium">👤 Public Profile</p>
                  <p className="text-sm text-slate-500">Others can view your profile</p>
                </div>
                <Switch checked={privacySettings.showProfile} onCheckedChange={(v) => setPrivacySettings({...privacySettings, showProfile: v})} />
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium">📊 Activity Visibility</p>
                  <p className="text-sm text-slate-500">Show your buying/selling activity</p>
                </div>
                <Switch checked={privacySettings.showActivity} onCheckedChange={(v) => setPrivacySettings({...privacySettings, showActivity: v})} />
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium">💬 Allow Messages</p>
                  <p className="text-sm text-slate-500">Let others message you</p>
                </div>
                <Switch checked={privacySettings.allowMessages} onCheckedChange={(v) => setPrivacySettings({...privacySettings, allowMessages: v})} />
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium">📢 Personalized Ads</p>
                  <p className="text-sm text-slate-500">Share data for better ads</p>
                </div>
                <Switch checked={privacySettings.shareDataForAds} onCheckedChange={(v) => setPrivacySettings({...privacySettings, shareDataForAds: v})} />
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium">🤖 AI Training</p>
                  <p className="text-sm text-slate-500">Help improve our AI features</p>
                </div>
                <Switch checked={privacySettings.aiTraining} onCheckedChange={(v) => setPrivacySettings({...privacySettings, aiTraining: v})} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPrivacy(false)}>Cancel</Button>
              <Button onClick={handleSavePrivacy} className="bg-emerald-500 hover:bg-emerald-600">
                Save Settings 🔒
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Language Dialog */}
        <Dialog open={showLanguage} onOpenChange={setShowLanguage}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                🌐 Select Language
              </DialogTitle>
            </DialogHeader>
            <div className="grid gap-2 py-4">
              {Object.entries(LANGUAGES).map(([code, info]) => (
                <button
                  key={code}
                  onClick={() => { changeLanguage(code); toast.success(`Language changed to ${info.name}! 🌐`); setShowLanguage(false); }}
                  className={`p-3 rounded-lg text-left flex items-center gap-3 transition-colors ${
                    i18n.language === code 
                      ? 'bg-emerald-100 border-2 border-emerald-500' 
                      : 'bg-slate-50 hover:bg-slate-100 border-2 border-transparent'
                  }`}
                >
                  <span className="text-2xl">{info.flag}</span>
                  <span className="font-medium">{info.name}</span>
                  {i18n.language === code && <Check className="h-4 w-4 text-emerald-600 ml-auto" />}
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* Currency Dialog */}
        <Dialog open={showCurrency} onOpenChange={setShowCurrency}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                💱 Select Currency
              </DialogTitle>
            </DialogHeader>
            <div className="grid gap-2 py-4">
              {[
                { code: 'USD', name: 'US Dollar', symbol: '$', flag: '🇺🇸' },
                { code: 'EUR', name: 'Euro', symbol: '€', flag: '🇪🇺' },
                { code: 'GBP', name: 'British Pound', symbol: '£', flag: '🇬🇧' },
                { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$', flag: '🇨🇦' },
                { code: 'AUD', name: 'Australian Dollar', symbol: 'A$', flag: '🇦🇺' },
              ].map((currency) => (
                <button
                  key={currency.code}
                  onClick={() => handleCurrencyChange(currency.code)}
                  className={`p-3 rounded-lg text-left flex items-center gap-3 transition-colors ${
                    userCurrency?.code === currency.code 
                      ? 'bg-emerald-100 border-2 border-emerald-500' 
                      : 'bg-slate-50 hover:bg-slate-100 border-2 border-transparent'
                  }`}
                >
                  <span className="text-2xl">{currency.flag}</span>
                  <div>
                    <p className="font-medium">{currency.name}</p>
                    <p className="text-sm text-slate-500">{currency.symbol}</p>
                  </div>
                  {userCurrency?.code === currency.code && <Check className="h-4 w-4 text-emerald-600 ml-auto" />}
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Account Dialog */}
        <Dialog open={showDeleteAccount} onOpenChange={(open) => {
          setShowDeleteAccount(open);
          if (!open) setDeleteConfirmText('');
        }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                ⚠️ Close Account
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-slate-600 mb-4">
                Are you sure you want to permanently delete your account? This action cannot be undone.
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-700 font-medium mb-2">You will lose:</p>
                <ul className="text-sm text-red-600 space-y-1">
                  <li>• All your listings and sales history</li>
                  <li>• Your feedback and seller rating</li>
                  <li>• Your watchlist and saved items</li>
                  <li>• Access to any pending transactions</li>
                </ul>
              </div>
              <Label>Type "DELETE" to confirm:</Label>
              <Input 
                className="mt-2" 
                placeholder="DELETE" 
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteAccount(false)} disabled={deletingAccount}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== 'DELETE' || deletingAccount}
              >
                {deletingAccount ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Deleting...
                  </>
                ) : (
                  'Delete Account'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Offers Dialog */}
        <Dialog open={showOffers} onOpenChange={setShowOffers}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                🎁 My Offers
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <Tabs defaultValue="received" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-4">
                  <TabsTrigger value="received" className="flex items-center gap-2">
                    📥 Received
                    {offers.pending_received > 0 && (
                      <Badge className="bg-amber-100 text-amber-700 text-xs">{offers.pending_received}</Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="sent" className="flex items-center gap-2">
                    📤 Sent
                    {offers.pending_sent > 0 && (
                      <Badge className="bg-blue-100 text-blue-700 text-xs">{offers.pending_sent}</Badge>
                    )}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="received">
                  {loadingOffers ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
                    </div>
                  ) : offers.received.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <p className="text-4xl mb-2">📭</p>
                      <p>No offers received yet</p>
                      <p className="text-sm">When buyers make offers on your listings, they'll appear here</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {offers.received.map((offer) => (
                        <Card key={offer.message_id} className="p-4">
                          <div className="flex items-start gap-4">
                            {offer.listing?.images?.[0] ? (
                              <img 
                                src={offer.listing.images[0]} 
                                alt="" 
                                className="w-16 h-16 rounded-lg object-cover cursor-pointer"
                                onClick={() => navigate(`/listing/${offer.listing_id}`)}
                              />
                            ) : (
                              <div className="w-16 h-16 rounded-lg bg-slate-100 flex items-center justify-center">
                                📦
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-medium truncate">{offer.listing?.title || 'Item'}</p>
                                {offer.message_type === 'bundle_offer' && (
                                  <Badge className="bg-purple-100 text-purple-700 text-xs">
                                    🎁 Bundle · {offer.bundle?.listings?.length || offer.bundle_listing_ids?.length || 'multiple'} items
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-slate-500">From: {offer.sender?.name || 'Buyer'}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-lg font-bold text-emerald-600">${offer.offer_amount?.toFixed(2)}</span>
                                {offer.message_type === 'bundle_offer' && offer.bundle?.original_total ? (
                                  <span className="text-sm text-slate-400 line-through">${offer.bundle.original_total.toFixed(2)}</span>
                                ) : (offer.listing?.price ? (
                                  <span className="text-sm text-slate-400 line-through">${offer.listing.price.toFixed(2)}</span>
                                ) : null)}
                                {offer.message_type === 'bundle_offer' && offer.bundle?.discount_percent ? (
                                  <Badge variant="outline" className="text-xs text-emerald-700 border-emerald-200">
                                    {offer.bundle.discount_percent}% off
                                  </Badge>
                                ) : null}
                              </div>
                              {offer.content && (
                                <p className="text-sm text-slate-600 mt-2 italic whitespace-pre-line">"{offer.content}"</p>
                              )}
                              <div className="flex items-center gap-2 mt-2">
                                <Badge className={
                                  offer.offer_status === 'pending' ? 'bg-amber-100 text-amber-700' :
                                  offer.offer_status === 'accepted' ? 'bg-emerald-100 text-emerald-700' :
                                  offer.offer_status === 'declined' ? 'bg-red-100 text-red-700' :
                                  'bg-blue-100 text-blue-700'
                                }>
                                  {offer.offer_status?.charAt(0).toUpperCase() + offer.offer_status?.slice(1)}
                                </Badge>
                                <span className="text-xs text-slate-400">
                                  {new Date(offer.created_at).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                          </div>
                          {offer.offer_status === 'pending' && (
                            <div className="flex gap-2 mt-4 pt-4 border-t">
                              <Button 
                                size="sm" 
                                className="bg-emerald-600 hover:bg-emerald-700"
                                onClick={() => handleOfferResponse(offer, 'accepted')}
                                data-testid={`offer-accept-${offer.message_id}`}
                              >
                                <Check className="h-4 w-4 mr-1" /> Accept
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleOfferResponse(offer, 'declined')}
                                data-testid={`offer-decline-${offer.message_id}`}
                              >
                                <X className="h-4 w-4 mr-1" /> Decline
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  const counter = prompt('Enter your counter offer amount:', offer.offer_amount);
                                  if (counter && !isNaN(parseFloat(counter))) {
                                    handleOfferResponse(offer, 'countered', parseFloat(counter));
                                  }
                                }}
                              >
                                💬 Counter
                              </Button>
                            </div>
                          )}
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="sent">
                  {loadingOffers ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
                    </div>
                  ) : offers.sent.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <p className="text-4xl mb-2">📤</p>
                      <p>No offers sent yet</p>
                      <p className="text-sm">When you make offers on listings, they'll appear here</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {offers.sent.map((offer) => (
                        <Card key={offer.message_id} className="p-4">
                          <div className="flex items-start gap-4">
                            {offer.listing?.images?.[0] ? (
                              <img 
                                src={offer.listing.images[0]} 
                                alt="" 
                                className="w-16 h-16 rounded-lg object-cover cursor-pointer"
                                onClick={() => navigate(`/listing/${offer.listing_id}`)}
                              />
                            ) : (
                              <div className="w-16 h-16 rounded-lg bg-slate-100 flex items-center justify-center">
                                📦
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-medium truncate">{offer.listing?.title || 'Item'}</p>
                                {offer.message_type === 'bundle_offer' && (
                                  <Badge className="bg-purple-100 text-purple-700 text-xs">
                                    🎁 Bundle · {offer.bundle?.listings?.length || offer.bundle_listing_ids?.length || 'multiple'} items
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-slate-500">To: {offer.seller?.name || offer.listing?.seller_name || 'Seller'}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-lg font-bold text-emerald-600">${offer.offer_amount?.toFixed(2)}</span>
                                {offer.message_type === 'bundle_offer' && offer.bundle?.original_total ? (
                                  <span className="text-sm text-slate-400 line-through">${offer.bundle.original_total.toFixed(2)}</span>
                                ) : (offer.listing?.price ? (
                                  <span className="text-sm text-slate-400 line-through">${offer.listing.price.toFixed(2)}</span>
                                ) : null)}
                                {offer.message_type === 'bundle_offer' && offer.bundle?.discount_percent ? (
                                  <Badge variant="outline" className="text-xs text-emerald-700 border-emerald-200">
                                    {offer.bundle.discount_percent}% off
                                  </Badge>
                                ) : null}
                              </div>
                              {offer.content && (
                                <p className="text-sm text-slate-600 mt-2 italic whitespace-pre-line">"{offer.content}"</p>
                              )}
                              <div className="flex items-center gap-2 mt-2">
                                <Badge className={
                                  offer.offer_status === 'pending' ? 'bg-amber-100 text-amber-700' :
                                  offer.offer_status === 'accepted' ? 'bg-emerald-100 text-emerald-700' :
                                  offer.offer_status === 'declined' ? 'bg-red-100 text-red-700' :
                                  'bg-blue-100 text-blue-700'
                                }>
                                  {offer.offer_status?.charAt(0).toUpperCase() + offer.offer_status?.slice(1)}
                                </Badge>
                                <span className="text-xs text-slate-400">
                                  {new Date(offer.created_at).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                          </div>
                          {offer.offer_status === 'accepted' && (
                            <div className="mt-4 pt-4 border-t">
                              <Button 
                                size="sm" 
                                className="bg-emerald-600 hover:bg-emerald-700"
                                onClick={() => navigate(`/listing/${offer.listing_id}`)}
                              >
                                Complete Purchase
                              </Button>
                            </div>
                          )}
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </DialogContent>
        </Dialog>

        {/* Payments Dialog */}
        <Dialog open={showPayments} onOpenChange={setShowPayments}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                💳 Payout Methods
              </DialogTitle>
              <p className="text-sm text-slate-500">
                Set up how you want to receive money from your sales
              </p>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* PayPal Section - OAuth Flow */}
              <div className={`p-4 rounded-lg border-2 transition-colors ${
                paypalConnected && paypalVerified ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200'
              }`}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-2xl">🅿️</span>
                  <div className="flex-1">
                    <p className="font-medium">PayPal (for receiving payouts)</p>
                    <p className="text-sm text-slate-500">
                      {paypalConnected && paypalVerified 
                        ? `✓ Connected: ${paypalEmail}` 
                        : 'Connect your PayPal account to receive payouts'}
                    </p>
                    {paypalPayerId && (
                      <p className="text-xs text-slate-400">Payer ID: {paypalPayerId}</p>
                    )}
                  </div>
                  {paypalConnected && paypalVerified && <Check className="h-5 w-5 text-emerald-600" />}
                </div>
                
                {paypalConnected && paypalVerified ? (
                  <div className="space-y-2">
                    <div className="bg-white p-3 rounded-lg border text-center">
                      <p className="text-sm text-emerald-600 font-medium">PayPal Account Connected</p>
                      <p className="text-xs text-slate-500 mt-1">{paypalEmail}</p>
                    </div>
                    <Button 
                      variant="outline"
                      onClick={handleDisconnectPayPal}
                      className="w-full text-red-500 hover:text-red-600 hover:bg-red-50"
                      disabled={connectingPaypal}
                    >
                      {connectingPaypal ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Disconnect PayPal
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-slate-400">
                      Click below to securely connect your PayPal account. You'll be redirected to PayPal to authorize.
                    </p>
                    <Button 
                      onClick={handleConnectPayPal}
                      className="w-full bg-[#0070ba] hover:bg-[#005ea6] text-white"
                      disabled={connectingPaypal}
                      data-testid="connect-paypal-btn"
                    >
                      {connectingPaypal ? (
                        <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Connecting...</>
                      ) : (
                        <>
                          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.771.771 0 0 1 .76-.654h6.751c2.235 0 3.947.498 5.087 1.479 1.082.93 1.527 2.233 1.325 3.876-.074.584-.217 1.165-.426 1.734a8.238 8.238 0 0 1-.775 1.553 6.09 6.09 0 0 1-1.132 1.322c-.453.39-.961.704-1.517.936-.6.25-1.26.423-1.965.516-.69.091-1.454.137-2.278.137H9.11a.775.775 0 0 0-.76.653l-.86 5.449a.641.641 0 0 1-.634.536l.22.08z"/>
                          </svg>
                          Connect with PayPal
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
              
              <div className="text-center text-slate-400 text-sm">— or —</div>
              
              <button
                onClick={handleConnectStripe}
                disabled={connectingStripe}
                className={`w-full p-4 rounded-lg border-2 flex items-center gap-3 transition-colors ${
                  payoutSettings?.stripe_connected ? 'border-purple-500 bg-purple-50' : 'border-slate-200 hover:border-purple-300'
                }`}
              >
                <span className="text-2xl">🏦</span>
                <div className="text-left flex-1">
                  <p className="font-medium">Direct Bank Transfer (Stripe)</p>
                  <p className="text-sm text-slate-500">
                    {payoutSettings?.stripe_connected ? 'Bank account connected ✓' : 'Connect your bank for direct deposits'}
                  </p>
                </div>
                {payoutSettings?.stripe_connected ? (
                  <Check className="h-5 w-5 text-purple-600" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-slate-400" />
                )}
              </button>
              
              <p className="text-xs text-slate-400 text-center mt-4">
                💡 You'll receive payouts 3-7 days after your item is delivered to the buyer
              </p>
            </div>
          </DialogContent>
        </Dialog>

        {/* Seller Payouts Dialog */}
        <Dialog open={showSellerPayouts} onOpenChange={setShowSellerPayouts}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                💵 Seller Payouts
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {loadingPayouts ? (
                <div className="text-center py-8">
                  <div className="animate-spin h-8 w-8 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto"></div>
                  <p className="text-sm text-slate-500 mt-2">Loading...</p>
                </div>
              ) : (
                <>
                  {/* Balance Summary */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-4 text-center">
                      <p className="text-sm text-slate-600">Available</p>
                      <p className="text-2xl font-bold text-emerald-600">
                        ${sellerBalance?.available_balance?.toFixed(2) || '0.00'}
                      </p>
                      <p className="text-xs text-slate-500">Ready to withdraw</p>
                    </div>
                    <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-4 text-center">
                      <p className="text-sm text-slate-600">Pending</p>
                      <p className="text-2xl font-bold text-amber-600">
                        ${sellerBalance?.pending_balance?.toFixed(2) || '0.00'}
                      </p>
                      <p className="text-xs text-slate-500">{sellerBalance?.hold_days || 3}-day hold</p>
                    </div>
                  </div>
                  
                  <div className="bg-slate-50 rounded-lg p-3 text-center">
                    <p className="text-sm text-slate-600">Total Earnings</p>
                    <p className="text-xl font-bold text-slate-800">
                      ${sellerBalance?.total_earnings?.toFixed(2) || '0.00'}
                    </p>
                    <p className="text-xs text-slate-500">
                      ${sellerBalance?.total_paid_out?.toFixed(2) || '0.00'} paid out
                    </p>
                  </div>

                  {/* Payout Method Setup */}
                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-3">💳 Payout Methods</h4>
                    
                    {/* PayPal */}
                    <div className="space-y-3">
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium flex items-center gap-2">
                            🅿️ PayPal
                            {payoutSettings?.paypal_connected && (
                              <Badge className="bg-emerald-100 text-emerald-700 text-xs">Connected</Badge>
                            )}
                          </span>
                        </div>
                        <Input
                          placeholder="your@paypal.email"
                          value={payoutSettings?.paypal_email || ''}
                          onChange={(e) => setPayoutSettings({...payoutSettings, paypal_email: e.target.value})}
                          className="mb-2"
                        />
                        <Button 
                          size="sm" 
                          onClick={() => handleUpdatePayoutSettings(payoutSettings?.paypal_email)}
                          className="bg-blue-500 hover:bg-blue-600 w-full"
                        >
                          {payoutSettings?.paypal_connected ? 'Update PayPal' : 'Connect PayPal'}
                        </Button>
                      </div>
                      
                      {/* Stripe Connect */}
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium flex items-center gap-2">
                            💳 Stripe Connect
                            {payoutSettings?.stripe_connected && (
                              <Badge className="bg-emerald-100 text-emerald-700 text-xs">Connected</Badge>
                            )}
                          </span>
                        </div>
                        <p className="text-sm text-slate-500 mb-2">
                          Get paid directly to your bank account
                        </p>
                        <Button 
                          size="sm" 
                          onClick={handleConnectStripe}
                          disabled={connectingStripe}
                          className="bg-purple-500 hover:bg-purple-600 w-full"
                        >
                          {connectingStripe ? 'Connecting...' : (payoutSettings?.stripe_connected ? 'Manage Stripe' : 'Connect Stripe')}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Request Payout Button */}
                  {sellerBalance?.available_balance > 0 && (payoutSettings?.paypal_connected || payoutSettings?.stripe_connected) && (
                    <div className="space-y-2">
                      {user?.kyc_status !== 'verified' ? (
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="text-sm text-amber-800 font-medium">
                            ⚠️ Identity verification required for payout
                          </p>
                          <p className="text-xs text-amber-600 mt-1">
                            Complete ID verification in Account Settings to withdraw your funds.
                          </p>
                          <Button 
                            onClick={() => setActiveTab('settings')}
                            variant="outline"
                            size="sm"
                            className="mt-2 border-amber-300 text-amber-700 hover:bg-amber-100"
                          >
                            Verify Identity
                          </Button>
                        </div>
                      ) : (
                        <Button 
                          onClick={handleRequestPayout}
                          disabled={requestingPayout}
                          className="w-full bg-emerald-500 hover:bg-emerald-600"
                        >
                          {requestingPayout ? 'Processing...' : `💸 Request Payout ($${sellerBalance.available_balance.toFixed(2)})`}
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Pending Releases */}
                  {sellerBalance?.pending_releases?.length > 0 && (
                    <div className="border-t pt-4">
                      <h4 className="font-medium mb-2">⏳ Upcoming Releases</h4>
                      <div className="space-y-2">
                        {sellerBalance.pending_releases.map((release, i) => (
                          <div key={i} className="flex justify-between text-sm p-2 bg-amber-50 rounded">
                            <span>${release.amount?.toFixed(2)}</span>
                            <span className="text-slate-500">
                              {new Date(release.hold_until).toLocaleDateString()}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Payout History */}
                  {payoutHistory.length > 0 && (
                    <div className="border-t pt-4">
                      <h4 className="font-medium mb-2">📜 Recent Sales</h4>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {payoutHistory.slice(0, 10).map((payout, i) => (
                          <div key={i} className="flex items-center justify-between text-sm p-2 bg-slate-50 rounded">
                            <div>
                              <p className="font-medium truncate max-w-[180px]">{payout.listing_title}</p>
                              <p className="text-xs text-slate-500">{new Date(payout.purchased_at).toLocaleDateString()}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-medium text-emerald-600">${payout.seller_payout?.toFixed(2)}</p>
                              <Badge className={
                                payout.payout_status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                                payout.delivery_status === 'delivered' ? 'bg-blue-100 text-blue-700' :
                                'bg-amber-100 text-amber-700'
                              }>
                                {payout.payout_status === 'paid' ? '✓ Paid' : 
                                 payout.delivery_status === 'delivered' ? 'On Hold' : 'Pending'}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* KYC Verification Modal */}
        <KYCVerificationModal
          isOpen={showKYCModal}
          onClose={() => setShowKYCModal(false)}
          onSuccess={() => {
            setShowKYCModal(false);
            // Refresh user data
            window.location.reload();
          }}
        />

      </div>
    </div>
  );
};

export default AccountPage;
